import { useLocation, useRoute } from 'wouter'
import { ChevronLeft, Calendar, Clock, MapPin, Loader2, Share2, DollarSign } from 'lucide-react'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Button } from '@/components/ui/button'
import useSWR from 'swr'
import { apiFetch } from '@/utils'
import { useToast } from '@/hooks/use-toast'
import { getImageUrls } from '@/utils/image-urls'
import GalleryGrid, { GalleryImage } from '@/components/GalleryGrid'

interface EventDetail {
  id: number | string
  heading: string
  page_title?: string
  page_description?: string
  content?: string
  date_start?: string
  date_end?: string
  event_dates?: string
  location?: string
  price_text?: string
  filename?: string
  filename_alt?: string
  page_url?: string
  published_date?: string
  category_id?: number | null
  gallery?: GalleryImage[]
}

const LoadingState = () => {
  return (
    <div className="flex-1 flex flex-col items-center justify-center">
      <Loader2 className="h-8 w-8 text-[#EF4444] animate-spin" />
      <p className="text-[#BABABB] text-sm mt-4">Loading event details...</p>
    </div>
  )
}

const ErrorState = ({ message }: { message?: string }) => {
  const [, navigate] = useLocation()

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6">
      <p className="text-[#EF4444] text-center mb-4">
        {message || 'Failed to load event. Please try again later.'}
      </p>
      <Button
        onClick={() => navigate('/events')}
        className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
      >
        Back to Events
      </Button>
    </div>
  )
}

export default function EventDetailPage() {
  const [, navigate] = useLocation()
  const [match, params] = useRoute('/events/:id')
  const { toast } = useToast()

  const eventId = params?.id

  const { data, error, isLoading } = useSWR(
    eventId ? `/v1/secrets_hideaway/events/${eventId}` : null,
    (url: string) => apiFetch(url, { withCredentials: true }).then((res) => res.json())
  )

  const event: EventDetail | null = data?.success && data?.details?.items?.[0] ? data.details.items[0] : null
  const imageUrls = event?.filename ? getImageUrls('events', event.filename) : null

  const formattedDate = event?.date_start
    ? new Date(event.date_start).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : event?.event_dates

  const handleShare = async () => {
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({
          title: event?.heading || 'Event',
          url
        })
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      await navigator.clipboard.writeText(url)
      toast({
        title: 'Link copied',
        description: 'Event link copied to clipboard'
      })
    }
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-[#18181B] flex flex-col">
        <div className="max-w-3xl mx-auto w-full px-5 pb-8">
          {/* Header with back button */}
          <div
            role="banner"
            className="h-12 flex items-center justify-center text-white mb-4 pt-3 relative"
          >
            <button
              type="button"
              aria-label="Go back"
              onClick={() => navigate('/events')}
              className="absolute left-0
                h-8 w-8
                flex items-center justify-center
                rounded-full
                bg-white/10 hover:bg-white/20
                focus:outline-none focus:ring-2 focus:ring-white/40"
            >
              <ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
            </button>

            <h1 className="text-base font-semibold tracking-wide">
              Event Details
            </h1>

            {event && (
              <button
                type="button"
                aria-label="Share event"
                onClick={handleShare}
                className="absolute right-0
                  h-8 w-8
                  flex items-center justify-center
                  rounded-full
                  bg-white/10 hover:bg-white/20
                  focus:outline-none focus:ring-2 focus:ring-white/40"
              >
                <Share2 className="h-[18px] w-[18px] text-[#ffffff]" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <LoadingState />
        ) : error || !event ? (
          <ErrorState />
        ) : (
          <div className="flex-1 max-w-3xl mx-auto w-full px-5 pb-8">
            {/* Event Image */}
            {imageUrls && (
              <div className="w-full overflow-hidden rounded-xl mb-6">
                <img
                  src={imageUrls.poster}
                  alt={event.filename_alt || event.heading}
                  className="w-full h-auto"
                />
              </div>
            )}

            {/* Title */}
            <h2 className="text-[#ffffff] font-bold text-2xl mb-4">
              {event.heading}
            </h2>

            {/* Event Meta Card */}
            <div className="bg-[#3C3C3C] rounded-xl p-4 mb-6 space-y-3">
              {formattedDate && (
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#EF4444]/20 flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-[#EF4444]" />
                  </div>
                  <div>
                    <p className="text-[#ffffff] font-medium">{formattedDate}</p>
                  </div>
                </div>
              )}

              {event.location && (
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#EF4444]/20 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-[#EF4444]" />
                  </div>
                  <div>
                    <p className="text-[#ffffff] font-medium">{event.location}</p>
                  </div>
                </div>
              )}

              {event.price_text && (
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#EF4444]/20 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-[#EF4444]" />
                  </div>
                  <div>
                    <p className="text-[#ffffff] font-medium">{event.price_text}</p>
                    <p className="text-[#BABABB] text-sm">Entry fee</p>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            {event.page_description && (
              <div className="mb-6">
                <h3 className="text-[#ffffff] font-semibold text-lg mb-2">About</h3>
                <p className="text-[#BABABB] leading-relaxed whitespace-pre-wrap">
                  {event.page_description}
                </p>
              </div>
            )}

            {/* Full Content */}
            {event.content && (
              <div className="prose prose-invert max-w-none">
                <div
                  className="text-[#BABABB] leading-relaxed [&>p]:mb-4"
                  dangerouslySetInnerHTML={{ __html: event.content }}
                />
              </div>
            )}

            {/* Gallery Grid */}
            {event.gallery && event.gallery.length > 0 && (
              <GalleryGrid images={event.gallery} title="Gallery" />
            )}
          </div>
        )}
      </div>
    </ErrorBoundary>
  )
}
